import { Component, effect, HostListener, QueryList, ViewChildren } from '@angular/core';
import { Store } from '@ngrx/store';
import { RouterModule } from '@angular/router';
import { DashboardHeaderComponent } from '@app/layout/components/dashboard-header/dashboard-header.component';
import { DashboardType } from '@app/core/enums/dashboard-types.enum';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SharedModule } from '@app/shared/shared.module';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { AppState } from '@app/core/store/app.state';
import {
  LoadReferralAccountSettingsRequested,
  UpdateReferralAccountSettingsRequested,
} from '@app/features/referralDashboard/store/referral-account-settings/referral-account-settings.actions';
import {
  selectReferralAccountSettingsLastSaveResult,
  selectReferralUser
} from '@app/features/referralDashboard/store/referral-account-settings/referral-account-settings.selectors';
import {
  ClinicSearchDropdownComponent
} from '@app/shared/components/clinic-search-dropdown/clinic-search-dropdown.component';
import { ProviderMultiselectComponent } from '@app/shared/components/provider-multiselect/provider-multiselect.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReferralUser } from '@app/core/interfaces/user.interface';
import {
  ChangePasswordModalComponent
} from '@app/shared/interfaces/change-password-modal/change-password-modal.component';
import { ContactUsInfoComponent } from '@app/shared/components/contact-us-info/contact-us-info.component';
import {
  AccountPersonalInfoFormComponent
} from '@app/shared/components/account-personal-info-form/account-personal-info-form.component';
import { UnsavedChangesGuard } from '@app/shared/guards/unsaved-changes.guard';
import { ReferralDashboardService } from '@app/features/referralDashboard/services/referral-dashboard.service';
import { ToastService } from '@app/core/services/toast.service';
import { map, Observable, take } from 'rxjs';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-work-comp-account-preferences',
  providers: [
    provideNgxMask()
  ],
  imports: [
    RouterModule,
    DashboardHeaderComponent,
    MatCardModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatDialogModule,
    SharedModule,
    NgxMaskDirective,
    ClinicSearchDropdownComponent,
    ProviderMultiselectComponent,
    ContactUsInfoComponent,
    AccountPersonalInfoFormComponent
],
  templateUrl: './referral-account-preferences.component.html',
  styleUrl: './referral-account-preferences.component.scss'
})
export class ReferralAccountPreferencesComponent implements UnsavedChangesGuard {
  readonly dashboardType = DashboardType.Referral;
  @ViewChildren(ProviderMultiselectComponent) providerMultiselectComponents!: QueryList<ProviderMultiselectComponent>;

  accountForm!: FormGroup;
  companyInfoForm!: FormGroup;
  clinicInfoForm!: FormGroup;

  initialAccountFormValues: unknown;
  initialCompanyInfoFormValues: unknown;
  initialClinicInfoFormValues: unknown;

  originalFirstClinicId!: string | undefined;
  firstClinicCtrl!: FormControl;
  firstProvidersCtrl!: FormControl;
  originalSecondClinicId!: string | undefined;
  secondClinicCtrl!: FormControl;
  secondProvidersCtrl!: FormControl;
  firstClinicOtherProviderNameCtrl!: FormControl;
  firstClinicOtherProviderNPICtrl!: FormControl;
  firstClinicRemoveProviderCtrl!: FormControl;
  secondClinicOtherProviderNameCtrl!: FormControl;
  secondClinicOtherProviderNPICtrl!: FormControl;
  secondClinicRemoveProviderCtrl!: FormControl;
  displayFirstClinicProviderIds: string[] = [];
  displaySecondClinicProviderIds: string[] = [];
  displayFirstPendingRemovalProviderIds: string[] = [];
  displaySecondPendingRemovalProviderIds: string[] = [];

  displayHasFirstOtherProvider: boolean = false;
  displayHasSecondOtherProvider: boolean = false;

  userData = toSignal(this.store.select(selectReferralUser));
  updateResult = toSignal(this.store.select(selectReferralAccountSettingsLastSaveResult));

  pendingUpdateForm: 'account' | 'company' | 'clinic' | null = null;
  showClinicPendingApprovalBanner = false;
  secondClinicRemovalRequested = false;
  discardClinicPendingChangesInProgress = false;
  private lastProcessedUpdateResult: unknown;

  constructor(
    private fb: FormBuilder,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private referralDashboardService: ReferralDashboardService,
    private toastService: ToastService,
  ) {
    this.initializeForms();
    this.loadUser();
    this.patchForm();
    this.listenForUpdate();
  }

  canDeactivate(): boolean | Observable<boolean> {
    if (!this.accountForm.dirty && !this.companyInfoForm.dirty && !this.clinicInfoForm.dirty) {
      return true;
    }

    let confirmed = false;
    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Unsaved Changes';
    dialog.componentInstance.content = 'You have unsaved changes. Are you sure you want to leave this page?';
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => confirmed = true);
    return dialog.afterClosed().pipe(map(() => confirmed));
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.accountForm?.dirty || this.companyInfoForm.dirty || this.clinicInfoForm.dirty) {
      event.preventDefault();
      event.returnValue = ''; // required for modern browsers
      return '';
    }
    return undefined;
  }

  onPreferencesFormSubmit(): void {
    if (this.accountForm.valid) {

      const updatedUser = {
        ...this.userData(),
        firstName: this.accountForm.value.firstName,
        lastName: this.accountForm.value.lastName,
        phoneNumber: this.accountForm.value.phone,
        jobTitle: this.accountForm.value.jobTitle
      };

      this.pendingUpdateForm = 'account';
      this.store.dispatch(UpdateReferralAccountSettingsRequested({ user: updatedUser as ReferralUser }));
    }
  }

  onCompanyInfoFormSubmit(): void {
    if (this.companyInfoForm.valid) {

      const updatedUser = {
        ...this.userData(),
        managerFirstName: this.companyInfoForm.value.managerFirstName,
        managerLastName: this.companyInfoForm.value.managerLastName,
        managerEmail: this.companyInfoForm.value.managerEmail,
        faxForInformation: this.companyInfoForm.value.managerContactFaxNumber,
        faxForChartNote: this.companyInfoForm.value.managerChartNotesFaxNumber,
      };

      this.pendingUpdateForm = 'company';
      this.store.dispatch(UpdateReferralAccountSettingsRequested({ user: updatedUser as ReferralUser }));
    }
  }

  onClinicInfoFormSubmit(): void {
    if (!this.clinicInfoForm.valid) {
      return;
    }

    this.secondClinicRemovalRequested = !!this.originalSecondClinicId && !this.secondClinicCtrl.value;

    const updatedUser = {
      ...this.userData(),
      firstClinicId: this.firstClinicCtrl.value,
      firstClinicProviderIds: this.firstProvidersCtrl.value,
      firstClinicOtherProviderName: this.firstClinicOtherProviderNameCtrl.value,
      firstClinicOtherProviderNPI: this.firstClinicOtherProviderNPICtrl.value,
      requestedRemovalFirstClinicProviderIds: this.firstClinicRemoveProviderCtrl.value,
      secondClinicId: this.secondClinicCtrl.value,
      requestedRemovalSecondClinicId: this.secondClinicRemovalRequested ? this.originalSecondClinicId : null,
      secondClinicProviderIds: this.secondProvidersCtrl.value,
      secondClinicOtherProviderName: this.secondClinicOtherProviderNameCtrl.value,
      secondClinicOtherProviderNPI: this.secondClinicOtherProviderNPICtrl.value,
      requestedRemovalSecondClinicProviderIds: this.secondClinicRemoveProviderCtrl.value
    };
    this.pendingUpdateForm = 'clinic';
    this.store.dispatch(UpdateReferralAccountSettingsRequested({ user: updatedUser as ReferralUser }));
  }

  onPreferencesCancel(): void {
    this.confirmAndReset(this.accountForm, this.initialAccountFormValues);
  }

  onCompanyInfoCancel(): void {
    this.confirmAndReset(this.companyInfoForm, this.initialCompanyInfoFormValues);
  }

  onClinicInfoCancel(): void {
    this.confirmAndReset(this.clinicInfoForm, this.initialClinicInfoFormValues, () => {
      this.secondClinicRemovalRequested = false;
      Promise.resolve().then(() => {
        this.providerMultiselectComponents.forEach((providerMultiselectComponent) => {
          providerMultiselectComponent.syncStateFromFormControls();
        });
      });
    });
  }

  onRemoveSecondClinic(): void {
    this.secondClinicRemovalRequested = true;
    this.secondClinicCtrl.setValue(null);

    [
      this.secondProvidersCtrl,
      this.secondClinicOtherProviderNameCtrl,
      this.secondClinicOtherProviderNPICtrl
    ].forEach((control) => {
      control.clearValidators();
      control.setValue(null);
      control.updateValueAndValidity({ emitEvent: false });
    });

    this.secondClinicRemoveProviderCtrl.setValue([]);
    this.secondClinicCtrl.markAsDirty();
    this.secondClinicCtrl.markAsTouched();
  }

  onDiscardClinicPendingChanges(): void {
    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Discard Changes';
    dialog.componentInstance.content = 'Discard changes and return to your previous settings?';
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => {
        if (this.discardClinicPendingChangesInProgress) {
          return;
        }

        this.discardClinicPendingChangesInProgress = true;

        this.referralDashboardService.discardReferralUserPendingClinicProviderChanges$().subscribe({
          next: (result) => {
            this.discardClinicPendingChangesInProgress = false;
            if (result?.succeeded) {
              this.toastService.showSuccessToast('Pending clinic and provider changes were discarded.');
              this.secondClinicRemovalRequested = false;
              this.loadUser();
              return;
            }
            this.toastService.showErrorToast('Unable to discard pending clinic and provider changes.');
          },
          error: () => {
            this.discardClinicPendingChangesInProgress = false;
            this.toastService.showErrorToast('Unable to discard pending clinic and provider changes.');
          }
        });
      });
  }

  openChangePasswordModal(): void {
    this.dialog.open(ChangePasswordModalComponent);
  }

  private confirmAndReset(form: FormGroup, initialValues: unknown, onConfirm?: () => void): void {
    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Unsaved Changes';
    dialog.componentInstance.content = 'You have unsaved changes. Are you sure you want to cancel?';
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => {
        form.reset(initialValues);
        form.markAsPristine();
        onConfirm?.();
      });
  }

  private initializeForms(): void {
    this.accountForm = this.buildAccountForm();
    this.companyInfoForm = this.buildCompanyInfoForm();
    this.clinicInfoForm = this.buildClinicInfoForm();

    this.initialAccountFormValues = this.accountForm.getRawValue();
    this.initialCompanyInfoFormValues = this.companyInfoForm.getRawValue();
    this.initialClinicInfoFormValues = this.clinicInfoForm.getRawValue();

    this.setClinicInfoFormValues();
  }

  private loadUser(): void {
    this.store.dispatch(LoadReferralAccountSettingsRequested());
  }

  private patchForm(): void {
    effect(() => {
      const user = this.userData();

      if (!user) {
        return;
      }

      this.accountForm.patchValue({
        firstName: user.firstName ?? null,
        lastName: user.lastName ?? null,
        phone: user.phoneNumber ?? null,
        email: user.email ?? null,
        jobTitle: user.jobTitle ?? null
      });

      this.initialAccountFormValues = this.accountForm.getRawValue();

      // ngx-mask is marking phone number as dirty when patching, so manually mark it as pristine.
      // Claimed to have been fixed in ngx-mask v17.0.8, but still seeing the issue
      // https://github.com/JsDaddy/ngx-mask/issues/1357
      this.accountForm.markAsPristine();

      this.companyInfoForm.patchValue({
        managerFirstName: user.managerFirstName ?? null,
        managerLastName: user.managerLastName ?? null,
        managerEmail: user.managerEmail ?? null,
        managerContactFaxNumber: user.faxForInformation ?? null,
        managerChartNotesFaxNumber: user.faxForChartNote ?? null,
      });

      this.initialCompanyInfoFormValues = this.companyInfoForm.getRawValue();
      this.companyInfoForm.markAsPristine();

      this.displayFirstClinicProviderIds = this.getCurrentClinicProviderIds(
        user.firstClinicProviderIds,
        user.pendingFirstClinicProviderIds,
        user.pendingRemovalFirstClinicProviderIds
      );
      this.displaySecondClinicProviderIds = this.getCurrentClinicProviderIds(
        user.secondClinicProviderIds,
        user.pendingSecondClinicProviderIds,
        user.pendingRemovalSecondClinicProviderIds
      );
      this.displayFirstPendingRemovalProviderIds = [...(user.pendingRemovalFirstClinicProviderIds ?? [])];
      this.displaySecondPendingRemovalProviderIds = [...(user.pendingRemovalSecondClinicProviderIds ?? [])];

      this.displayHasFirstOtherProvider = user.firstClinicOtherProviderName !== null && user.firstClinicOtherProviderNPI !== null;
      this.displayHasSecondOtherProvider = user.secondClinicOtherProviderName !== null && user.secondClinicOtherProviderNPI !== null;
      const hasPendingSecondClinicRemoval = this.hasPendingSecondClinicRemoval(user);
      this.secondClinicRemovalRequested = hasPendingSecondClinicRemoval;

      this.clinicInfoForm.patchValue({
        firstClinicId: user.firstClinicId ?? null,
        firstClinicProviderIds: this.displayFirstClinicProviderIds,
        firstClinicRemoveProvider: user.requestedRemovalFirstClinicProviderIds ?? [],
        firstClinicOtherProviderName: user.firstClinicOtherProviderName ?? null,
        firstClinicOtherProviderNPI: user.firstClinicOtherProviderNPI ?? null,
        secondClinicId: hasPendingSecondClinicRemoval ? null : user.secondClinicId ?? null,
        secondClinicProviderIds: this.displaySecondClinicProviderIds,
        secondClinicRemoveProvider: user.requestedRemovalSecondClinicProviderIds ?? [],
        secondClinicOtherProviderName: user.secondClinicOtherProviderName ?? null,
        secondClinicOtherProviderNPI: user.secondClinicOtherProviderNPI ?? null
      });

      this.initialClinicInfoFormValues = this.clinicInfoForm.getRawValue();
      this.originalFirstClinicId = user.firstClinicId ?? undefined;
      this.originalSecondClinicId = user.pendingRemovalSecondClinicId ?? user.secondClinicId ?? undefined;
      this.showClinicPendingApprovalBanner = this.hasPendingClinicProviderChanges(user);
      this.setClinicInfoFormValues();
      this.setClinicInfoFormReadOnly(this.showClinicPendingApprovalBanner);
      this.clinicInfoForm.markAsPristine();
    });
  }

  private listenForUpdate(): void {
    effect(() => {
      const updateResult = this.updateResult();
      if (!updateResult || updateResult === this.lastProcessedUpdateResult) {
        return;
      }

      if (updateResult?.succeeded) {
        if (this.pendingUpdateForm === 'account') {
          this.initialAccountFormValues = this.accountForm.getRawValue();
          this.accountForm.markAsPristine();
        } else if (this.pendingUpdateForm === 'company') {
          this.initialCompanyInfoFormValues = this.companyInfoForm.getRawValue();
          this.companyInfoForm.markAsPristine();
        } else if (this.pendingUpdateForm === 'clinic') {
          // Keep clinic section locked while we refresh server state.
          this.showClinicPendingApprovalBanner = true;
          this.setClinicInfoFormReadOnly(true);
          this.clinicInfoForm.markAsPristine();
          this.loadUser();
        }
        this.pendingUpdateForm = null;
      }
      this.lastProcessedUpdateResult = updateResult;
    });
  }

  private buildAccountForm(): FormGroup {
    return this.fb.group({
      firstName: this.fb.control<string | null>(null, Validators.required),
      lastName: this.fb.control<string | null>(null, Validators.required),
      email: this.fb.control<string | null>({ value: null, disabled: true }, [Validators.required, Validators.email]),
      phone: this.fb.control<string | null>(null, Validators.required),
      jobTitle: this.fb.control<string | null>(null, Validators.required),
    });
  }

  private buildCompanyInfoForm(): FormGroup {
    return this.fb.group({
      managerFirstName: this.fb.control<string | null>(null, Validators.required),
      managerLastName: this.fb.control<string | null>(null, Validators.required),
      managerEmail: this.fb.control<string | null>(null, [Validators.required, Validators.email]),
      managerContactFaxNumber: this.fb.control<string | null>(null, Validators.required),
      managerChartNotesFaxNumber: this.fb.control<string | null>(null, Validators.required),
    });
  }

  private buildClinicInfoForm(): FormGroup {
    return this.fb.group({
      firstClinicId: this.fb.control<string | null>(null, Validators.required),
      firstClinicProviderIds: this.fb.control<string[] | null>([], Validators.required),
      secondClinicId: this.fb.control<string | null>(null),
      secondClinicProviderIds: this.fb.control<string[] | null>([]),
      firstClinicRemoveProvider: this.fb.control<string[] | null>([]),
      secondClinicRemoveProvider: this.fb.control<string[] | null>([]),
      firstClinicOtherProviderName: this.fb.control<string | null>(null),
      firstClinicOtherProviderNPI: this.fb.control<string | null>(null),
      secondClinicOtherProviderName: this.fb.control<string | null>(null),
      secondClinicOtherProviderNPI: this.fb.control<string | null>(null)

    });
  }

  private setClinicInfoFormValues(): void {
    this.firstClinicCtrl = this.clinicInfoForm.controls['firstClinicId'] as FormControl;
    this.firstProvidersCtrl = this.clinicInfoForm.controls['firstClinicProviderIds'] as FormControl;
    this.secondClinicCtrl = this.clinicInfoForm.controls['secondClinicId'] as FormControl;
    this.secondProvidersCtrl = this.clinicInfoForm.controls['secondClinicProviderIds'] as FormControl;
    this.firstClinicOtherProviderNameCtrl = this.clinicInfoForm.controls['firstClinicOtherProviderName'] as FormControl;
    this.firstClinicOtherProviderNPICtrl = this.clinicInfoForm.controls['firstClinicOtherProviderNPI'] as FormControl;
    this.secondClinicOtherProviderNameCtrl = this.clinicInfoForm.controls['secondClinicOtherProviderName'] as FormControl;
    this.firstClinicRemoveProviderCtrl = this.clinicInfoForm.controls['firstClinicRemoveProvider'] as FormControl;
    this.secondClinicOtherProviderNPICtrl = this.clinicInfoForm.controls['secondClinicOtherProviderNPI'] as FormControl;
    this.secondClinicRemoveProviderCtrl = this.clinicInfoForm.controls['secondClinicRemoveProvider'] as FormControl;
  }

  private setClinicInfoFormReadOnly(readOnly: boolean): void {
    if (readOnly) {
      this.clinicInfoForm.disable({ emitEvent: false });
    } else {
      this.clinicInfoForm.enable({ emitEvent: false });
    }
  }

  private hasPendingClinicProviderChanges(user: ReferralUser): boolean {
    return (user.pendingFirstClinicProviderIds?.length ?? 0) > 0
      || (user.pendingRemovalFirstClinicProviderIds?.length ?? 0) > 0
      || (user.requestedRemovalFirstClinicProviderIds?.length ?? 0) > 0
      || (user.pendingSecondClinicProviderIds?.length ?? 0) > 0
      || (user.pendingRemovalSecondClinicProviderIds?.length ?? 0) > 0
      || (user.requestedRemovalSecondClinicProviderIds?.length ?? 0) > 0
      || this.hasPendingSecondClinicRemoval(user)
      || (!!user.pendingFirstClinicOtherProviderName && !!user.pendingFirstClinicOtherProviderNPI)
      || (!!user.pendingSecondClinicOtherProviderName && !!user.pendingSecondClinicOtherProviderNPI);
  }

  private hasPendingSecondClinicRemoval(user: ReferralUser): boolean {
    return !!user.pendingRemovalSecondClinicId;
  }

  private getCurrentClinicProviderIds(
    approvedProviderIds?: string[],
    pendingProviderIds?: string[],
    pendingRemovalProviderIds?: string[]
  ): string[] {
    const mergedProviderIds = new Set<string>(approvedProviderIds ?? []);

    for (const pendingRemovalProviderId of pendingRemovalProviderIds ?? []) {
      mergedProviderIds.delete(pendingRemovalProviderId);
    }

    for (const pendingProviderId of pendingProviderIds ?? []) {
      mergedProviderIds.add(pendingProviderId);
    }

    return [...mergedProviderIds];
  }
}
